import { useCallback, useEffect, useMemo, useState } from 'react'
import { Check, ChevronRight, ImagePlus, Sparkles, UserRound } from 'lucide-react'
import { client, type PhotoInfo } from '../lib/api'
import {
  CONNECTION_INTENT_LABEL,
  CONNECTION_INTENT_OPTIONS,
  GENDER_IDENTITY_OPTIONS,
  GENDER_INTEREST_OPTIONS,
  Logo,
  OUTREACH_LABEL,
  OUTREACH_OPTIONS,
  parseInterestedIn,
  type ConnectionIntent,
  type GenderIdentity,
  type GenderInterest,
  type OutreachPreference,
} from '../lib/ui'
import { SelfieVerification } from './SelfieVerification'
import { ProfilePhotoGrid } from './ProfilePhotoGrid'
import {
  SocialLinksEditor,
  emptySocialLinksForm,
  socialLinksInputFromForm,
  type SocialLinksFormState,
} from './SocialLinks'
import type { MeUser } from './Settings'
import type { ProfileCompletionSnapshot } from '../../shared/onboarding'
import { MAX_PROFILE_PHOTOS, ONBOARDING_MIN_BIO_LENGTH, ONBOARDING_MIN_PHOTOS } from '../../shared/onboarding'

type Step = 'welcome' | 'profile' | 'photos' | 'verify' | 'review'

const STEPS: Step[] = ['welcome', 'profile', 'photos', 'verify', 'review']

function stepIndex(step: Step): number {
  return STEPS.indexOf(step)
}

function ProgressRing({ percent }: { percent: number }) {
  const r = 28
  const c = 2 * Math.PI * r
  const offset = c - (percent / 100) * c
  return (
    <div className="onboarding-progress-ring" aria-label={`${percent}% complete`}>
      <svg width="72" height="72" viewBox="0 0 72 72">
        <circle cx="36" cy="36" r={r} fill="none" stroke="var(--border)" strokeWidth="6" />
        <circle
          cx="36"
          cy="36"
          r={r}
          fill="none"
          stroke="var(--accent)"
          strokeWidth="6"
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={offset}
          transform="rotate(-90 36 36)"
        />
      </svg>
      <strong>{percent}%</strong>
    </div>
  )
}

export function OnboardingWizard({
  user,
  initialCompletion,
  onComplete,
  onLogout,
  notify,
}: {
  user: MeUser
  initialCompletion: ProfileCompletionSnapshot | null
  onComplete: () => void
  onLogout: () => void
  notify: (msg: string) => void
}) {
  const [step, setStep] = useState<Step>('welcome')
  const [completion, setCompletion] = useState<ProfileCompletionSnapshot | null>(initialCompletion)
  const [busy, setBusy] = useState(false)

  const [city, setCity] = useState(user.city ?? '')
  const [birthYear, setBirthYear] = useState(user.birth_year ? String(user.birth_year) : '')
  const [bio, setBio] = useState(user.bio ?? '')
  const [outreach, setOutreach] = useState<OutreachPreference>(
    user.outreach_preference ?? 'either',
  )
  const [intent, setIntent] = useState<ConnectionIntent>(user.connection_intent ?? 'exploring')
  const [socialLinksForm, setSocialLinksForm] = useState<SocialLinksFormState>(emptySocialLinksForm)
  const [gender, setGender] = useState<GenderIdentity | ''>((user.gender as GenderIdentity) ?? '')
  const [interestedIn, setInterestedIn] = useState<GenderInterest[]>(() => parseInterestedIn(user.interested_in))

  const [photos, setPhotos] = useState<PhotoInfo[]>([])
  const [profileError, setProfileError] = useState<string | null>(null)
  const [stepInitialized, setStepInitialized] = useState(false)

  const refreshCompletion = useCallback(async () => {
    const res = await client.onboardingStatus()
    setCompletion(res.onboarding)
    if (res.onboarding.complete) onComplete()
    return res.onboarding
  }, [onComplete])

  const loadPhotos = useCallback(async () => {
    const detail = await client.profileDetail(user.id)
    setPhotos(detail.photos.filter((p) => p.kind === 'public'))
  }, [user.id])

  useEffect(() => {
    void loadPhotos().catch(() => undefined)
  }, [loadPhotos])

  useEffect(() => {
    if (!completion) void refreshCompletion().catch(() => undefined)
  }, [completion, refreshCompletion])

  useEffect(() => {
    if (!completion || stepInitialized) return
    if (completion.complete) {
      setStep('review')
    } else if (!completion.steps.find((s) => s.id === 'profile')?.done) {
      setStep('profile')
    } else if (!completion.steps.find((s) => s.id === 'photos')?.done) {
      setStep('photos')
    } else if (!completion.steps.find((s) => s.id === 'verified')?.done) {
      setStep('verify')
    } else {
      setStep('review')
    }
    setStepInitialized(true)
  }, [completion, stepInitialized])

  const percent = completion?.percent ?? 0

  const bioOk = bio.trim().length >= ONBOARDING_MIN_BIO_LENGTH
  const yearNum = Number(birthYear)
  const yearOk = Number.isFinite(yearNum) && yearNum >= 1900 && yearNum <= new Date().getFullYear() - 18
  const cityOk = city.trim().length >= 2
  const profileFormOk = bioOk && yearOk && cityOk && !!gender

  const toggleGenderInterest = (current: GenderInterest[], value: GenderInterest): GenderInterest[] => {
    if (value === 'everyone') return ['everyone']
    const withoutEveryone = current.filter((v) => v !== 'everyone')
    if (withoutEveryone.includes(value)) {
      const next = withoutEveryone.filter((v) => v !== value)
      return next.length ? next : ['everyone']
    }
    return [...withoutEveryone, value]
  }

  const saveProfile = async () => {
    setProfileError(null)
    if (!profileFormOk) {
      if (!cityOk) setProfileError('Enter your city')
      else if (!yearOk) setProfileError('Enter a valid birth year — you must be 18+')
      else if (!gender) setProfileError('Choose how you identify')
      else if (!bioOk) setProfileError(`Bio needs at least ${ONBOARDING_MIN_BIO_LENGTH} characters (${bio.trim().length} so far)`)
      return
    }
    setBusy(true)
    try {
      await client.updateProfile({
        city: city.trim(),
        birthYear: yearNum,
        bio: bio.trim(),
        gender,
        interestedIn,
        outreachPreference: outreach,
        connectionIntent: intent,
        socialLinks: socialLinksInputFromForm(socialLinksForm),
      })
      await refreshCompletion()
      setStep('photos')
      notify('Profile saved')
    } catch (e) {
      const msg = (e as Error).message
      setProfileError(msg)
      notify(msg)
    } finally {
      setBusy(false)
    }
  }

  const uploadPhoto = async (file: File) => {
    setBusy(true)
    try {
      const res = await client.uploadPhoto(file, 'public')
      await loadPhotos()
      await refreshCompletion()
      notify(res.message ?? 'Photo uploaded')
    } catch (e) {
      notify((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  const replacePhoto = async (photoId: string, file: File) => {
    setBusy(true)
    try {
      const res = await client.replacePhoto(photoId, file)
      await loadPhotos()
      await refreshCompletion()
      notify(res.message ?? 'Photo updated')
    } catch (e) {
      notify((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  const deletePhoto = async (photoId: string) => {
    setBusy(true)
    try {
      await client.deletePhoto(photoId)
      await loadPhotos()
      await refreshCompletion()
      notify('Photo deleted')
    } catch (e) {
      notify((e as Error).message)
      throw e
    } finally {
      setBusy(false)
    }
  }

  const reorderPhotos = async (photoIds: string[]) => {
    setBusy(true)
    try {
      await client.reorderPhotos(photoIds)
      await loadPhotos()
    } catch (e) {
      notify((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  const publicPhotoCount = photos.length
  const canContinuePhotos = publicPhotoCount >= ONBOARDING_MIN_PHOTOS

  const stepLabel = useMemo(() => {
    const i = stepIndex(step)
    return `Step ${Math.max(1, i)} of ${STEPS.length - 1}`
  }, [step])

  if (completion?.complete) {
    return (
      <div className="onboarding-shell">
        <div className="onboarding-card onboarding-done">
          <ProgressRing percent={100} />
          <h1>You're all set</h1>
          <p>Your profile is complete and visible in discovery. Same rules for everyone — interest has to mean something.</p>
          <button type="button" className="button primary" onClick={onComplete}>
            Start discovering
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="onboarding-shell">
      <header className="onboarding-header">
        <Logo compact />
        <ProgressRing percent={percent} />
      </header>

      <div className="onboarding-card">
        <p className="onboarding-kicker">{step === 'welcome' ? 'Welcome' : stepLabel}</p>

        {step === 'welcome' && (
          <>
            <h1>Hi, {user.display_name}</h1>
            <p className="page-sub">
              A few quick steps before you can browse. Incomplete profiles aren't shown — this keeps Pairvelle
              trustworthy for everyone.
            </p>
            <ul className="onboarding-checklist">
              <li><UserRound size={16} /> About you — city, age, bio</li>
              <li><ImagePlus size={16} /> {ONBOARDING_MIN_PHOTOS} clear photos (face visible)</li>
              <li><Sparkles size={16} /> Face Check matched to your photos</li>
            </ul>
            <div className="onboarding-actions">
              <button type="button" className="button primary" onClick={() => setStep('profile')}>
                Let's go <ChevronRight size={16} />
              </button>
              <button type="button" className="button secondary" onClick={onLogout}>
                Sign out
              </button>
            </div>
          </>
        )}

        {step === 'profile' && (
          <>
            <h1>Tell people about you</h1>
            <p className="page-sub">Keep it real — this is what others see besides your photos.</p>
            <div className="onboarding-form">
              <div className="field">
                <label htmlFor="ob-city">City</label>
                <input id="ob-city" className="input" value={city} onChange={(e) => setCity(e.target.value)} placeholder="Where you're based" />
              </div>
              <div className="field">
                <label htmlFor="ob-year">Birth year</label>
                <input id="ob-year" className="input" inputMode="numeric" value={birthYear} onChange={(e) => setBirthYear(e.target.value)} placeholder="1996" />
              </div>
              <div className="field">
                <label htmlFor="ob-bio">Short bio</label>
                <textarea id="ob-bio" className="input" rows={4} value={bio} onChange={(e) => { setBio(e.target.value); setProfileError(null) }} placeholder="Your vibe, what you create, what you're open to…" />
                <span className={`field-hint ${bio.trim().length > 0 && !bioOk ? 'field-hint-warn' : ''}`}>
                  {bio.trim().length}/{ONBOARDING_MIN_BIO_LENGTH} min · no links or phone numbers in bio
                </span>
              </div>
              <div className="field">
                <label>I am a</label>
                <div className="segmented">
                  {GENDER_IDENTITY_OPTIONS.map((opt) => (
                    <button key={opt.value} type="button" className={gender === opt.value ? 'selected' : ''} onClick={() => { setGender(opt.value); setProfileError(null) }}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="field">
                <label>Interested in</label>
                <div className="chip-row">
                  {GENDER_INTEREST_OPTIONS.map((opt) => {
                    const active = interestedIn.includes(opt.value)
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        className={`chip ${active ? 'selected' : ''}`}
                        onClick={() => setInterestedIn((current) => toggleGenderInterest(current, opt.value))}
                      >
                        {opt.label}
                      </button>
                    )
                  })}
                </div>
              </div>
              <div className="field">
                <label>Outreach</label>
                <div className="segmented">
                  {OUTREACH_OPTIONS.map((opt) => (
                    <button key={opt.value} type="button" className={outreach === opt.value ? 'selected' : ''} onClick={() => setOutreach(opt.value)}>
                      {OUTREACH_LABEL[opt.value]}
                    </button>
                  ))}
                </div>
              </div>
              <div className="field">
                <label>What you're here for</label>
                <div className="segmented">
                  {CONNECTION_INTENT_OPTIONS.map((opt) => (
                    <button key={opt.value} type="button" className={intent === opt.value ? 'selected' : ''} onClick={() => setIntent(opt.value)}>
                      {CONNECTION_INTENT_LABEL[opt.value]}
                    </button>
                  ))}
                </div>
              </div>
              <div className="field">
                <label>Social links (optional)</label>
                <SocialLinksEditor value={socialLinksForm} onChange={setSocialLinksForm} compact />
              </div>
            </div>
            {profileError && (
              <div className="form-error" role="alert">
                {profileError}
              </div>
            )}
            <button type="button" className="button primary" disabled={busy || !profileFormOk} onClick={saveProfile}>
              {busy ? 'Saving…' : 'Continue'}
            </button>
          </>
        )}

        {step === 'photos' && (
          <>
            <h1>Add {ONBOARDING_MIN_PHOTOS} photos</h1>
            <p className="page-sub">
              Show your face clearly — no filters that hide who you are. Google profile photos count as your first
              photo when you sign in with Google.
            </p>
            <ProfilePhotoGrid
              photos={photos}
              minSlots={ONBOARDING_MIN_PHOTOS}
              maxPhotos={MAX_PROFILE_PHOTOS}
              busy={busy}
              manageable
              uploadLabel={publicPhotoCount >= ONBOARDING_MIN_PHOTOS ? 'Add another' : 'Upload photo'}
              hint={`${publicPhotoCount}/${ONBOARDING_MIN_PHOTOS} required · tap a photo to replace, reorder, or delete`}
              onUpload={uploadPhoto}
              onReplace={replacePhoto}
              onReorder={reorderPhotos}
              onDelete={deletePhoto}
              onNotify={notify}
            />
            <button type="button" className="button primary" style={{ marginTop: '1rem' }} disabled={!canContinuePhotos || busy} onClick={() => setStep('verify')}>
              Continue
            </button>
          </>
        )}

        {step === 'verify' && (
          <>
            <h1>Verify it's you</h1>
            <p className="page-sub">Face Check — live selfie matched to your profile photos. No legal ID, ever.</p>
            <SelfieVerification
              status={user.verification_status}
              notify={notify}
              onVerified={() => void refreshCompletion()}
              minPhotos={ONBOARDING_MIN_PHOTOS}
            />
            <button type="button" className="button secondary" style={{ marginTop: '1rem' }} onClick={() => setStep('photos')}>
              Back to photos
            </button>
          </>
        )}

        {step === 'review' && completion && (
          <>
            <h1>Almost there</h1>
            <p className="page-sub">
              {completion.blockers.length
                ? "Finish the items below — you won't appear in discovery until everything is complete."
                : 'Final checks…'}
            </p>
            <ul className="onboarding-steps-list">
              {completion.steps.map((s) => (
                <li key={s.id} className={s.done ? 'done' : ''}>
                  <span className="onboarding-step-icon">{s.done ? <Check size={14} /> : '○'}</span>
                  <div>
                    <strong>{s.label}</strong>
                    <small>{s.detail}</small>
                  </div>
                </li>
              ))}
            </ul>
            <button type="button" className="button primary" onClick={() => void refreshCompletion()}>
              Refresh status
            </button>
          </>
        )}
      </div>

      <p className="onboarding-footnote">
        Incomplete profiles are hidden from discovery until setup is finished.
      </p>
    </div>
  )
}
